import { dietKind } from '@core/utils/diet';
import { isUomId } from '@core/utils/lineUnits';
import {
  isVariantProductOutOfStock,
  isVariantSellable,
  normalizeVariantSkus,
} from '@core/utils/variantSkus';

// Project the bloated items.Items shape (~110 fields) onto a lean Product.
// Service callers always go through here so feature code never sees the
// backend-shaped object.
//
// The price field is industry-driven: the storefront for an online grocery
// reads `online_price`, a dine-in QR menu reads `dine_in_price`, etc. The
// resolver passes that field name in — mappers don't read industry config.

const PRICE_FIELDS = [
  'online_price',
  'dine_in_price',
  'takeaway_price',
  'pos_price',
  'kiosk_price',
  'qr_price',
  'aggregator_price',
  'selling_price',
];

function hasEntries(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') return value.trim() !== '' && value !== '0';
  return Boolean(value);
}

function pickPrice(raw, priceField) {
  if (priceField && raw[priceField] != null) return raw[priceField];
  for (const f of PRICE_FIELDS) {
    if (raw[f] != null) return raw[f];
  }
  return 0;
}

function findLocation(raw, locationId) {
  if (!Array.isArray(raw.locations) || raw.locations.length === 0) return null;
  // if (!locationId) return raw.locations[0] ?? null;
  return raw.locations.find((location) => location?.location_id === locationId) ?? raw.locations[0];
}

// Alternative units are configured per location. Location entries omit the
// resolved unit_details/uom_details, so those are filled in from the
// item-level entry for the same unit, and service item groups missing a
// price_delta inherit it from the item-level group with the same id. The
// item-level list is only a lookup for those fields: a location without
// conversions offers no alternative units.
function mergeServiceItemGroups(groups, fallbackGroups) {
  if (!Array.isArray(groups)) return fallbackGroups;
  if (!Array.isArray(fallbackGroups)) return groups;
  return groups.map((group) => {
    const fallback = fallbackGroups.find(
      (entry) => entry?.service_item_group_id === group?.service_item_group_id,
    );
    return fallback
      ? { ...fallback, ...group, price_delta: group.price_delta ?? fallback.price_delta }
      : group;
  });
}

function resolveAlternativeUnits(raw, location) {
  const itemLevel = Array.isArray(raw.alternative_units_and_conversions)
    ? raw.alternative_units_and_conversions
    : [];
  if (!Array.isArray(location?.alternative_units_and_conversions)) return [];

  return location.alternative_units_and_conversions.map((conversion) => {
    const fallback = itemLevel.find((entry) => entry?.unit === conversion?.unit);
    if (!fallback) return conversion;
    return {
      ...conversion,
      unit_details: conversion.unit_details ?? fallback.unit_details,
      uom_details: conversion.uom_details ?? fallback.uom_details,
      service_item_groups: mergeServiceItemGroups(
        conversion.service_item_groups,
        fallback.service_item_groups,
      ),
    };
  });
}

function findChannel(location, channelId) {
  if (!channelId || !Array.isArray(location?.channels)) return null;
  return location.channels.find((channel) => channel?.channel_id === channelId) ?? null;
}

function findVariantById(list, variant) {
  const variantId = variant?.variant_id ?? variant?.id;
  if (!variantId || !Array.isArray(list)) return null;
  return list.find((item) => (item?.variant_id ?? item?.id ?? item?.value) === variantId) ?? null;
}

function findModifierDetails(modifier) {
  return modifier?.modifier_details ?? modifier?.modifierdetails ?? modifier;
}

function resolveVariantOption(variant, { locationVariants, channelId } = {}) {
  const locationVariant = findVariantById(locationVariants, variant);
  const source = locationVariant ?? variant ?? {};
  const channel = findChannel(source, channelId);
  const baseVariantDelta = firstNumber(variant?.price_delta) ?? 0;
  const locationVariantDelta = locationVariant
    ? (firstNumber(locationVariant.price_delta) ?? 0)
    : 0;
  const channelVariantDelta = firstNumber(channel?.price_delta) ?? 0;

  return {
    ...variant,
    ...source,
    price: baseVariantDelta + locationVariantDelta + channelVariantDelta,
    price_delta: baseVariantDelta + locationVariantDelta + channelVariantDelta,
    channels: Array.isArray(source?.channels) ? source.channels : [],
  };
}

function mapResolvedVariants(list, opts) {
  return Array.isArray(list) ? list.map((variant) => resolveVariantOption(variant, opts)) : [];
}

function resolveModifierPrice(modifier, { locationId, channelId } = {}) {
  const details = findModifierDetails(modifier);
  const location = findLocation(details, locationId);
  const channels = Array.isArray(location?.channels) ? location.channels : [];
  const channel = channelId
    ? (channels.find((item) => item?.channel_id === channelId) ?? channels[0])
    : channels[0];

  return firstNumber(channel?.price, 0) ?? 0;
}

function resolveModifierOption(modifier, opts = {}) {
  const details = findModifierDetails(modifier);
  const resolvedPrice = resolveModifierPrice(modifier, opts);

  return {
    ...modifier,
    ...details,
    modifierdetails: modifier?.modifierdetails ?? details,
    price: resolvedPrice,
    price_delta: resolvedPrice,
  };
}

function mapResolvedModifiers(list, opts) {
  return Array.isArray(list) ? list.map((modifier) => resolveModifierOption(modifier, opts)) : [];
}

function resolveModifierGroup(group, opts = {}) {
  if (!group || typeof group !== 'object') return group;
  const modifiers = mapResolvedModifiers(group.modifiers, opts);

  return {
    ...group,
    ...(Array.isArray(group.modifiers) ? { modifiers } : {}),
  };
}

function mapResolvedModifierGroups(list, opts) {
  return Array.isArray(list) ? list.map((group) => resolveModifierGroup(group, opts)) : [];
}

// Prefer the backend's resolved channel/location price. The list endpoint
// returns a root selling_price that already includes the location delta, so
// re-adding deltas to it double-counts; the delta sum is only a fallback.
function resolveDeltaPrice(raw, { priceField, locationId, channelId } = {}) {
  const location = findLocation(raw, locationId);
  const channel = findChannel(location, channelId);
  const channelDelta = firstNumber(channel?.price_delta) ?? 0;

  const channelPrice = firstNumber(channel?.price);
  if (channelPrice !== undefined) return channelPrice;

  const locationPrice = firstNumber(location?.price);
  if (locationPrice !== undefined) return locationPrice + channelDelta;

  const locationDelta = firstNumber(location?.price_delta) ?? 0;
  return pickPrice(raw, priceField) + locationDelta + channelDelta;
}

function pickImage(raw) {
  const imageList = [...(Array.isArray(raw.images) ? raw.images : [])];
  const defaultImage = imageList.find((image) => image?.is_default) ?? imageList[0];
  return (
    raw.online_image?.url ??
    raw.OnlineImage?.url ??
    defaultImage?.url ??
    defaultImage?.image_url ??
    defaultImage ??
    raw.imageUrl ??
    raw.image_url ??
    raw.item_image_url ??
    raw.item_image ??
    raw.itemImage ??
    null
  );
}

function pickCategoryImage(raw) {
  const directImage =
    pickImage(raw) ?? raw.category_image?.url ?? raw.category_image_url ?? raw.image?.url ?? null;

  if (directImage) return directImage;

  const rawSubs = raw.subCategories ?? raw.sub_categories;
  const rawSubSubs = raw.subSubCategories ?? raw.sub_sub_categories;
  const children = [
    ...(Array.isArray(rawSubs) ? rawSubs : []),
    ...(Array.isArray(rawSubSubs) ? rawSubSubs : []),
  ];

  for (const child of children) {
    const childImage = pickCategoryImage(child);
    if (childImage) return childImage;
  }

  return null;
}

function getRawCategoryItemsCount(raw) {
  const count = Number(raw?.items_count ?? raw?.itemsCount ?? 0);
  return Number.isFinite(count) ? count : 0;
}

function getCategoryTreeItemsCount(raw) {
  if (!raw) return 0;

  const rawSubs = raw.subCategories ?? raw.sub_categories;
  const rawSubSubs = raw.subSubCategories ?? raw.sub_sub_categories;
  const children = [
    ...(Array.isArray(rawSubs) ? rawSubs : []),
    ...(Array.isArray(rawSubSubs) ? rawSubSubs : []),
  ];

  return children.reduce(
    (total, child) => total + getCategoryTreeItemsCount(child),
    getRawCategoryItemsCount(raw),
  );
}

function uniqueImages(...lists) {
  const seen = new Set();
  return lists
    .flat()
    .map((image) => (typeof image === 'string' ? image : image?.url))
    .filter((url) => {
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    });
}

function compactText(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** The item's food-type slug; any "not applicable" spelling becomes `na`. */
function pickFoodTypeSlug(raw) {
  const slug = compactText(raw.food_type_details?.slug ?? raw.food_indicator_details?.slug);
  return dietKind(slug) === 'na' ? 'na' : slug;
}

function pickName(details, fallback) {
  return compactText(details?.name) ?? compactText(fallback);
}

function toAvailability(value) {
  return Boolean(value);
}

function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return Boolean(value);
}

// The recipe's own variant_groups/variants (not the item's) carry per-variant
// ingredient overrides, keyed by variant_id. Flattens both into one lookup.
function collectRecipeVariantIngredients(recipeDetails) {
  const map = {};
  if (!recipeDetails) return map;
  const sources = Array.isArray(recipeDetails) ? recipeDetails : [recipeDetails];

  for (const recipeSource of sources) {
    if (!recipeSource || typeof recipeSource !== 'object') continue;
    const fromGroups = Array.isArray(recipeSource.variant_groups)
      ? recipeSource.variant_groups
      : [];
    for (const group of fromGroups) {
      const variants = Array.isArray(group?.variants) ? group.variants : [];
      for (const variant of variants) {
        const variantId = variant?.variant_id ?? variant?.id;
        if (variantId && Array.isArray(variant?.ingredients)) {
          map[variantId] = variant.ingredients;
        }
      }
    }

    const fromVariants = Array.isArray(recipeSource.variants) ? recipeSource.variants : [];
    for (const variant of fromVariants) {
      const variantId = variant?.variant_id ?? variant?.id;
      if (variantId && Array.isArray(variant?.ingredients)) {
        map[variantId] = variant.ingredients;
      }
    }
  }

  return map;
}

function extractRecipeIngredients(raw) {
  if (Array.isArray(raw.recipe) && raw.recipe.length > 0) return raw.recipe;
  if (Array.isArray(raw.ingredients) && raw.ingredients.length > 0) return raw.ingredients;

  const rawDetails = raw.recipe_details;
  if (!rawDetails) return [];

  if (Array.isArray(rawDetails)) {
    return rawDetails.flatMap((item) => {
      if (!item) return [];
      if (Array.isArray(item.ingredients)) return item.ingredients;
      if (item.ingredient_name || item.ingredient_id) return [item];
      return [];
    });
  }
  if (typeof rawDetails === 'object') {
    if (Array.isArray(rawDetails.ingredients)) return rawDetails.ingredients;
    if (rawDetails.ingredient_name || rawDetails.ingredient_id) return [rawDetails];
  }
  return [];
}

function mapNames(list) {
  return Array.isArray(list) ? list.map((item) => pickName(item, item)).filter(Boolean) : [];
}

/**
 * @param {object} raw items.Items shape
 * @param {{ priceField?: string }} [opts]
 * @returns {import('@core/types').Product}
 */
export function toProduct(raw, opts = {}) {
  const images = uniqueImages(raw.online_image?.url, Array.isArray(raw.images) ? raw.images : []);
  const activeLocation = findLocation(raw, opts.locationId);
  const activeLocationVariants = Array.isArray(activeLocation?.variants)
    ? activeLocation.variants
    : [];
  const availableStock = firstNumber(
    activeLocation?.available_quantity,
    activeLocation?.available_stock,
    raw.available_stock,
    raw.available_quantity,
    raw.online_available_stock,
    raw.stock,
  );
  const onlineTrackInventory = toBoolean(
    activeLocation?.online_track_inventory ?? raw.online_track_inventory,
  );
  const trackInventory = toBoolean(
    activeLocation?.track_inventory ?? raw.track_inventory ?? raw.online_track_inventory,
  );
  const sellableLevel = compactText(raw.sellable_level);
  // Variant-sellable items carry their modifiers per SKU; resolve them with
  // the same location/channel pricing as item-level modifiers.
  const variantSkus = Array.isArray(raw.variant_skus)
    ? normalizeVariantSkus(raw.variant_skus, {
        locationId: opts.locationId,
        channelId: opts.channelId,
      }).map((sku, index) => {
        const rawSku = raw.variant_skus[index];
        const modifierGroups = mapResolvedModifierGroups(rawSku?.modifier_groups, opts);
        const singleModifiers = mapResolvedModifiers(
          Array.isArray(rawSku?.single_modifiers) && rawSku.single_modifiers.length > 0
            ? rawSku.single_modifiers
            : rawSku?.single_modifier_details,
          opts,
        );
        return {
          ...sku,
          modifierGroups,
          singleModifiers,
          hasOwnModifiers: modifierGroups.length > 0 || singleModifiers.length > 0,
        };
      })
    : null;
  // Variant-sellable items: stock is decided per SKU only; the item's own
  // available_quantity / online_track_inventory are ignored.
  const isVariantItem = isVariantSellable({ sellableLevel });
  const isOutOfStock = isVariantItem
    ? isVariantProductOutOfStock(variantSkus)
    : onlineTrackInventory && availableStock !== undefined && availableStock <= 0;
  const isAvailable =
    !isOutOfStock && (raw.online_available ?? raw.offline_availability ?? raw.is_active ?? true);
  const hasVariants = [
    raw.variant_groups,
    raw.single_variants,
    raw.variants,
    raw.variant_group_ids,
    raw.variant_group_count,
    raw.variant_count,
    raw.has_variants,
  ].some(hasEntries);
  const hasModifiers =
    [
      raw.modifier_groups,
      raw.single_modifiers,
      raw.modifiers,
      raw.modifier_group_ids,
      raw.single_modifier_ids,
      raw.modifier_group_count,
      raw.modifier_count,
      raw.has_modifiers,
    ].some(hasEntries) || Boolean(variantSkus?.some((sku) => sku.hasOwnModifiers));
  // Combo data arrives as combo_id (id list) + combo_details (the actual
  // groups, each carrying combo_items) — confirmed against a live item
  // payload. combo_groups/combo_group_details are kept as fallbacks in case
  // another endpoint shape surfaces them under those names.
  const hasCombo = [
    raw.combo_id,
    raw.combo_details,
    raw.combo_groups,
    raw.combo_group_details,
    raw.combo_count,
    raw.has_combo,
  ].some(hasEntries);

  // Item-level upsells (detail payload only; list payloads send null).
  const list = (v) => (Array.isArray(v) ? v : []);
  const upsellGroupDetails = list(raw.upsell_group_details);
  const singleUpsellItemDetails = list(raw.single_upsell_item_details);
  const liveUpsell = (u) => u?.is_active !== false && u?.item_id;
  const hasUpsells =
    singleUpsellItemDetails.some(liveUpsell) ||
    upsellGroupDetails.some(
      (g) => g?.is_active !== false && list(g?.upsell_items).some(liveUpsell),
    );

  const recipeList = extractRecipeIngredients(raw);
  const recipeVariantIngredients = collectRecipeVariantIngredients(raw.recipe_details);
  const hasExcludableIngredients = recipeList.some((ing) => {
    return (
      ing?.is_excludable === true || ing?.is_excludable === 'true' || toBoolean(ing?.is_excludable)
    );
  });
  const hasRecipeDetails = Array.isArray(raw.recipe_details)
    ? raw.recipe_details.length > 0
    : Boolean(raw.recipe_details);

  return {
    id: raw.item_id ?? raw.id,
    name: raw.item_name ?? '',
    shortName: raw.short_name ?? undefined,
    description: raw.description ?? undefined,
    shortDescription: raw.short_description ?? undefined,
    // Variant-sellable items have no item-level price: the price belongs to
    // the chosen combination (variantSkus), so cards render none.
    price: isVariantItem ? undefined : resolveDeltaPrice(raw, opts),
    costPrice: raw.cost_price ?? undefined,
    profitMargin: raw.profit_margin ?? undefined,
    mrp: isVariantItem ? undefined : (raw.mrp ?? undefined),
    taxInclusive: Boolean(raw.taxInclusive),
    imageUrl: pickImage(raw) ?? images[0] ?? undefined,
    images,
    categoryId: raw.category_id ?? raw.category_details?.id ?? undefined,
    categoryName: raw.category_name ?? raw.category_details?.name ?? undefined,
    brandId: raw.brand_id ?? raw.brand_details?.id ?? undefined,
    brandName: raw.brand_name ?? raw.brand_details?.name ?? undefined,
    sku: raw.sku_code ?? raw.item_code ?? undefined,
    skuCode: compactText(raw.sku_code),
    itemCode: compactText(raw.item_code),
    itemType: pickName(raw.item_type_details),
    foodType: pickName(raw.food_type_details ?? raw.food_indicator_details),
    foodTypeSlug: pickFoodTypeSlug(raw) ?? undefined,
    taxGroupName: pickName(raw.tax_group_details),
    purchaseTaxGroupName: pickName(raw.purchase_tax_group_details),
    taxNames: mapNames(raw.tax_details ?? raw.taxes),
    purchaseTaxNames: mapNames(raw.purchase_tax_details ?? raw.purchase_taxes),
    // The raw `uom` field is a UOM-id (e.g. "UOM-202604-540"); prefer the
    // human label from uom_details. integer_place_unit is the lower-case
    // user-facing unit ("ml", "kg"); name is the same value upper-cased.
    uom:
      raw.uom_details?.name ??
      raw.uom_details?.short_code ??
      raw.uom_details?.integer_place_unit ??
      raw.uom_name ??
      raw.unit_name ??
      (typeof raw.uom === 'string' && !isUomId(raw.uom) ? raw.uom : undefined),
    uomId: raw.uom_details?.uom_id ?? raw.uom_id ?? (isUomId(raw.uom) ? raw.uom : undefined),
    uomDescription: compactText(raw.uom_details?.description),
    displayOrder: firstNumber(raw.display_order, raw.displayOrder, 0) ?? 0,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    isActive: raw.is_active ?? true,
    isAvailable,
    isOutOfStock,
    availableQuantity: availableStock,
    availableStock,
    trackInventory,
    onlineTrackInventory,
    onlineAvailability: toAvailability(raw.online_availability),
    offlineAvailability: toAvailability(raw.offline_availability),
    channels: [
      { key: 'pos', label: 'POS', available: toAvailability(raw.pos_available) },
      { key: 'qr', label: 'QR', available: toAvailability(raw.qr_available) },
      { key: 'kiosk', label: 'Kiosk', available: toAvailability(raw.kiosk_available) },
      { key: 'online', label: 'Online', available: toAvailability(raw.online_available) },
      {
        key: 'aggregator',
        label: 'Aggregator',
        available: toAvailability(raw.aggregator_available),
      },
    ],
    platformPricing: Array.isArray(raw.platform_pricing)
      ? raw.platform_pricing.map((platform) => ({
          id: platform.channel_id,
          name: platform.name,
          isActive: toAvailability(platform.is_active),
        }))
      : [],
    locations: Array.isArray(raw.locations)
      ? raw.locations.map((location) => ({
          id: location.location_id,
          name: location.name,
          isAvailable: toAvailability(location.is_available),
          price: location.price ?? undefined,
          availableQuantity: location.available_quantity ?? undefined,
          trackInventory: Boolean(location.track_inventory),
          channels: Array.isArray(location.channels)
            ? location.channels.map((channel) => ({
                id: channel.channel_id,
                name: channel.name,
                isActive: toAvailability(channel.is_active),
                price: channel.price ?? undefined,
                availableQuantity: channel.available_quantity ?? undefined,
              }))
            : [],
        }))
      : [],
    allergens: Array.isArray(raw.allergen_info) ? raw.allergen_info : [],
    dietaryTags: [
      ...(Array.isArray(raw.dietary_tags) ? raw.dietary_tags : []),
      pickFoodTypeSlug(raw),
    ].filter(Boolean),
    sellableLevel,
    variantSkus,
    variant_groups: Array.isArray(raw.variant_groups) ? raw.variant_groups : [],
    variant_group_details: Array.isArray(raw.variant_group_details)
      ? raw.variant_group_details
      : [],
    single_variants: mapResolvedVariants(raw.single_variants, {
      locationVariants: activeLocationVariants,
      channelId: opts.channelId,
    }),
    single_variant_details: mapResolvedVariants(raw.single_variant_details, {
      locationVariants: activeLocationVariants,
      channelId: opts.channelId,
    }),
    variants: mapResolvedVariants(
      Array.isArray(raw.single_variants) && raw.single_variants.length > 0
        ? raw.single_variants
        : Array.isArray(raw.single_variant_details) && raw.single_variant_details.length > 0
          ? raw.single_variant_details
          : raw.variants,
      {
        locationVariants: activeLocationVariants,
        channelId: opts.channelId,
      },
    ),
    modifier_groups: mapResolvedModifierGroups(raw.modifier_groups, opts),
    modifier_group_details: Array.isArray(raw.modifier_group_details)
      ? mapResolvedModifierGroups(raw.modifier_group_details, opts)
      : [],
    single_modifiers: Array.isArray(raw.single_modifiers)
      ? mapResolvedModifiers(raw.single_modifiers, opts)
      : Array.isArray(raw.single_modifier_details)
        ? mapResolvedModifiers(raw.single_modifier_details, opts)
        : [],
    single_modifier_details: Array.isArray(raw.single_modifier_details)
      ? mapResolvedModifiers(raw.single_modifier_details, opts)
      : Array.isArray(raw.single_modifiers)
        ? mapResolvedModifiers(raw.single_modifiers, opts)
        : [],
    modifiers: mapResolvedModifiers(raw.modifiers, opts),
    variant_group_ids: Array.isArray(raw.variant_group_ids) ? raw.variant_group_ids : [],
    modifier_group_ids: Array.isArray(raw.modifier_group_ids) ? raw.modifier_group_ids : [],
    single_modifier_ids: Array.isArray(raw.single_modifier_ids) ? raw.single_modifier_ids : [],
    alternative_units_and_conversions: resolveAlternativeUnits(raw, activeLocation),
    // Combo groups: raw.combo_details is an array of
    // { combo_id, name, min_select, max_select, combo_items: [...] }.
    // combo_groups/combo_group_details kept as fallbacks for other shapes.
    combo_groups: Array.isArray(raw.combo_details)
      ? raw.combo_details
      : Array.isArray(raw.combo_group_details)
        ? raw.combo_group_details
        : Array.isArray(raw.combo_groups)
          ? raw.combo_groups
          : [],
    // Base recipe / ingredient list for the item itself (not variant-scoped).
    // recipe_details, when present, is an array of recipe objects (usually
    // one) shaped { recipe_id, ingredients: [...], variant_groups: [...],
    // variants: [...] } — the ingredient list itself lives one level deeper
    // than recipe_details, and per-variant ingredient overrides live inside
    // that same recipe object's own variant_groups/variants, keyed by
    // variant_id (see recipeVariantIngredients below).
    recipe: recipeList,
    recipe_id:
      raw.recipe_id ||
      (Array.isArray(raw.recipe_details)
        ? raw.recipe_details[0]?.recipe_id
        : raw.recipe_details?.recipe_id) ||
      '',
    recipe_details: raw.recipe_details,
    recipeVariantIngredients: recipeVariantIngredients,
    upsell_group_details: upsellGroupDetails,
    single_upsell_item_details: singleUpsellItemDetails,
    hasVariants,
    hasModifiers,
    hasCombo,
    hasUpsells,
    hasExcludableIngredients,
    hasOptions:
      hasVariants ||
      hasModifiers ||
      hasCombo ||
      hasUpsells ||
      hasExcludableIngredients ||
      (hasRecipeDetails && recipeList.length > 0) ||
      hasEntries(raw.hasOptions),
    status: compactText(raw.status),
    priceType: compactText(raw.price_type),
    isPurchaseItem: Boolean(raw.is_purchase_item),
    isBatchTracked: Boolean(raw.is_batch_tracked),
    isExpiryTracked: Boolean(raw.is_expiry_tracked),
    isProduction: Boolean(raw.is_production),
    createdAt: compactText(raw.created_at),
    updatedAt: compactText(raw.updated_at),
  };
}

/**
 * Maps a single raw category node (without recursion).
 * @param {object} raw
 * @param {string | undefined} parentId
 * @returns {import('@core/types').Category}
 */
function mapCategoryNode(raw, parentId) {
  // The API sends parent_id: "" for root categories — normalise to undefined
  // so that !c.parentId correctly identifies top-level nodes.
  const resolvedParentId =
    parentId && parentId !== ''
      ? parentId
      : raw.parent_id && raw.parent_id !== ''
        ? raw.parent_id
        : undefined;
  return {
    id: raw.category_id ?? raw.id,
    name: raw.name ?? raw.category_name ?? '',
    imageUrl: pickCategoryImage(raw) ?? undefined,
    itemsCount: getCategoryTreeItemsCount(raw) || undefined,
    parentId: resolvedParentId,
    parentName: raw.parent_name ?? raw.parent_category_name ?? undefined,
    level: raw.level ?? 0,
    levelLabel: raw.level_label ?? undefined,
    slug: raw.slug ?? undefined,
    displayOrder: raw.display_order ?? undefined,
  };
}

/**
 * Recursively flattens a nested category tree returned by the API.
 * The API nests children inside `subCategories` and grandchildren inside
 * `subSubCategories`. This function walks the tree and returns a single
 * flat array with every node carrying its correct `parentId`.
 *
 * @param {object} raw top-level category from API
 * @returns {import('@core/types').Category[]}
 */
export function flattenCategory(raw) {
  const result = [];
  const parentId = raw.parent_id || undefined;
  const node = mapCategoryNode(raw, parentId);
  result.push(node);

  // Sub-categories (level 1 children)
  const rawSubs = raw.subCategories ?? raw.sub_categories;
  const subs = Array.isArray(rawSubs) ? rawSubs : [];
  for (const sub of subs) {
    const subNode = mapCategoryNode(sub, node.id);
    result.push(subNode);

    // Sub-sub-categories (level 2 grandchildren)
    const rawSubSubs = sub.subSubCategories ?? sub.sub_sub_categories;
    const subSubs = Array.isArray(rawSubSubs) ? rawSubSubs : [];
    for (const ss of subSubs) {
      result.push(mapCategoryNode(ss, subNode.id));
    }
  }
  return result;
}
/**
 * Legacy single-node mapper kept for backward compat.
 * Prefer flattenCategory() when the API returns nested trees.
 * @param {object} raw categories.Category shape
 * @returns {import('@core/types').Category}
 */
export function toCategory(raw) {
  return mapCategoryNode(raw, raw.parent_id ?? raw.parent_category_id ?? undefined);
}
/**
 * Sorts products by display_order ascending (0 comes first, then 1, 2, 3...).
 * Preserves original relative order for items with equal display_order.
 *
 * @param {import('@core/types').Product[]} products
 * @returns {import('@core/types').Product[]}
 */
export function sortByDisplayOrder(products) {
  if (!Array.isArray(products) || products.length <= 1) return products;

  return [...products].sort((a, b) => {
    const orderA = Number(a?.displayOrder ?? a?.display_order ?? 0);
    const orderB = Number(b?.displayOrder ?? b?.display_order ?? 0);
    return orderA - orderB;
  });
}
